"use client";

import Image from "next/image";
import { Input } from "@/components/ui/input";
import footerLogo from "/public/images/footer/kst-footer-logo.png";
import email from "/public/images/footer/email.png";
import { Facebook, Instagram, Youtube } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import ScrollToTop from "../ScrollToTop";
import { useTranslations } from "next-intl";

import { API_BASE, FOOTER_SUPER_PARTNER_SLUGS, PARTNER_SIGNUP_ENABLED } from "@/components/constants/general";

const SUBSCRIBE_ENDPOINT =
  `${API_BASE}/api/partner-requests/subscribe-request`;

const Footer = () => {
  const t = useTranslations("footer");

  // Resolved after mount (window isn't available during server render), so
  // the server HTML and first client render match — no hydration mismatch.
  const [vleHref, setVleHref] = useState("/DM-partner-register");
  useEffect(() => {
    const slug = FOOTER_SUPER_PARTNER_SLUGS[window.location.hostname];
    if (slug) setVleHref(`/DM-partner-register/${slug}`);
  }, []);

  const [subEmail, setSubEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async () => {
    const emailTrim = subEmail.trim();
    if (!emailTrim) {
      toast.error(t("emptyEmail"));
      return;
    }
    const okFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim);
    if (!okFormat) {
      toast.error(t("subscribeError"));
      return;
    }

    try {
      setSubmitting(true);
      const r = await fetch(SUBSCRIBE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email: emailTrim }),
      });

      const raw = await r.text();
      let data: any = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch { data = { message: raw }; }

      if (!r.ok) {
        if (r.status === 422 && data?.errors) {
          const firstKey = Object.keys(data.errors)[0];
          const firstMsg = Array.isArray(data.errors[firstKey])
            ? data.errors[firstKey][0]
            : data.errors[firstKey];
          toast.error(String(firstMsg || data.message || t("subscribeError")));
          return;
        }
        toast.error(String(data?.message || t("subscribeError")));
        return;
      }

      toast.success(t("subscribeSuccess"));
      setSubEmail("");
    } catch (e: any) {
      toast.error(e?.message ?? t("subscribeError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="bg-gray-600 text-white pt- bottom-0">
        <div className="container mx-auto px-4">
          <div className="section-padding border-warm-800 align-middle flex flex-col md:flex-row items-center flex-wrap py-5 gap-6">
            <div className="max-w-7xl mx-auto w-full">
              <div className="flex flex-col md:flex-row gap-4 items-center w-full">
                <p className="text-primary text-center md:text-left font-bold max-w-3xl w-full md:w-auto">
                  {t("subscribeTitle")}
                  <span className="text-gradient uppercase md:block font-bold">
                    {" "} {t("subscribeHighlight")}
                  </span>{" "}
                </p>
                <p className="text-warm-300 max-w-7xl w-full md:w-auto text-center md:text-left text-white">
                  {t("subscribeDesc")}
                </p>
                <div className="flex max-w-3xl w-full md:w-auto">
                  <Input
                    placeholder={t("emailPlaceholder")}
                    value={subEmail}
                    onChange={(e) => setSubEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !submitting) handleSubscribe();
                    }}
                    disabled={submitting}
                    className="bg-black/70 w-full md:w-[400px] border-white/20 text-white placeholder:text-white/60 rounded-tl-md rounded-br-lg"
                  />
                  <button
                    type="button"
                    onClick={handleSubscribe}
                    disabled={submitting}
                    aria-label="Subscribe"
                    className="hover:opacity-90 font-semibold px-2 rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Image
                      src="/images/footer/subscribe-button.png"
                      alt="Subscribe"
                      width={160}
                      height={64}
                      className="mr-2 inline h-10 w-10"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-neutral-950 text-white pt-10">
        <div className="container mx-auto px-4">
          <div className="py-16">
            <div className="flex flex-col lg:flex-row justify-between gap-12">
              <div className="w-full lg:w-[20%] flex justify-center lg:justify-start">
                <div className="flex items-center space-x-2 mb-6">
                  <Image
                    src={t("footerlogo")}
                    alt="Kissa Shuru Talkies logo"
                    width={240}
                    height={96}
                    className="h-auto w-auto"
                  />
                </div>
              </div>

              <div className="w-full lg:w-[52%]">
                <h2 className="text-lg font-semibold mb-6 text-left pl-4 md:pl-0">{t("quickLinks")}</h2>
                <div className="flex flex-wrap gap-6 justify-center lg:justify-start">
                  <div className="flex flex-col space-y-4 w-36">
                    <a href="/faq" className="text-warm-300 hover:text-white transition-colors">{t("links.faq")}</a>
                    {/* <a href="/testimonials" className="text-warm-300 hover:text-white transition-colors">{t("links.testimonials")}</a> */}
                    <a href="/grievance-redressal" className="text-warm-300 hover:text-white transition-colors">{t("links.grievance")}</a>
                    <a href="https://business.kissashurutalkies.com" target="_blank" rel="noopener noreferrer" className="text-warm-300 hover:text-white transition-colors">{t("links.producerLogin")}</a>
                    {PARTNER_SIGNUP_ENABLED && <a href={vleHref} className="text-warm-300 hover:text-white transition-colors">{t("links.vleRegistration")}</a>}
                  </div>
                  <div className="flex flex-col space-y-4 w-36">
                    <a href="/support" className="text-warm-300 hover:text-white transition-colors">{t("links.support")}</a>
                    <a href="/privacy-policy" className="text-warm-300 hover:text-white transition-colors">{t("links.privacy")}</a>
                    {/* <a href="/pricing" className="text-warm-300 hover:text-white transition-colors">{t("links.pricing")}</a> */}
                  </div>
                  <div className="flex flex-col space-y-4 w-36">
                    <a href="/terms-of-Use" className="text-warm-300 hover:text-white transition-colors">{t("links.terms")}</a>
                    <a href="/about" className="text-warm-300 hover:text-white transition-colors">{t("links.about")}</a>
                  </div>
                  <div className="flex flex-col space-y-4 w-36">
                    <a href="/contact" className="text-warm-300 hover:text-white transition-colors">{t("links.contact")}</a>
                    <a href="/franchise" className="text-warm-300 hover:text-white transition-colors">{t("links.franchise")}</a>
                  </div>
                </div>
              </div>

              <div className="w-full lg:w-[28%] flex justify-start">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 justify-start">
                    <div className="w-[48px] h-[48px] flex items-center justify-center flex-shrink-0">
                      <Image
                        src={email}
                        alt="Mail icon"
                        width={48}
                        height={48}
                        className="h-auto w-auto text-primary"
                      />
                    </div>
                    <div>
                      <span className="text-warm-300">{t("emailLabel")}</span>
                      <p>support@kissashurutalkies.com</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 justify-start">
                    <div className="w-[48px] h-[48px] flex items-center justify-center flex-shrink-0">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 448 512"
                        fill="#25D366"
                        className="w-11 h-11"
                      >
                        <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
                      </svg>
                    </div>
                    <div>
                      <span className="text-warm-300">{t("whatsappLabel")}</span>
                      <p>+91 92707 68020</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 justify-start">
                    <div className="w-[48px] h-[48px] flex items-center justify-center flex-shrink-0">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="#3B82F6"
                        className="w-11 h-11"
                      >
                        <path d="M12 1C5.93 1 1 5.93 1 12s4.93 11 11 11 11-4.93 11-11S18.07 1 12 1zm0 20c-4.97 0-9-4.03-9-9s4.03-9 9-9 9 4.03 9 9-4.03 9-9 9z"/>
                        <path d="M10.54 13.46a7.05 7.05 0 002.95 2.95l1.1-1.1a.5.5 0 01.5-.12c.56.18 1.16.28 1.78.28a.5.5 0 01.5.5v1.75a.5.5 0 01-.5.5A8.5 8.5 0 018.28 9.63a.5.5 0 01.5-.5h1.75a.5.5 0 01.5.5c0 .62.1 1.22.28 1.78a.5.5 0 01-.13.51l-1.1 1.1z" transform="translate(0.5, 0.5) scale(0.92)"/>
                      </svg>
                    </div>
                    <div>
                      <span className="text-warm-300">{t("contactLabel") || "Contact"}</span>
                      <p>+91 99207 51123</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr className="bg-warm-800 border-0 h-px my-0" />
          <div className="py-2 md:py-8 ">
            <div className="flex md:flex-row justify-between items-center gap-4 flex-col-reverse md:flex-wrap">
              <p className="text-warm-300 text-center md:text-left">
                {t("copyright")}
              </p>
              <div className="flex space-x-6 mt-4 md:mt-0 justify-center mb-2 mr-20">
                <div className="flex space-x-4 social">
                  <div className="bg-gray-500 px-2 py-2 rounded-full hover:bg-primary transition-colors">
                    <Link
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://www.facebook.com/kissashurutalkies/"
                      className="text-white hover:bg-white/10 rounded-full"
                      aria-label="Visit our Facebook page"
                    >
                      <Facebook className="w-5 h-5" />
                    </Link>
                  </div>

                  <div className="bg-gray-500 px-2 py-2 rounded-full hover:bg-primary transition-colors">
                    <Link
                      target="_blank"
                      href="https://www.instagram.com/kissashurutalkies"
                      className="text-white hover:bg-white/10 rounded-full"
                      aria-label="Visit our Instagram profile"
                    >
                      <Instagram className="w-5 h-5" />
                    </Link>
                  </div>

                  <div className="bg-gray-500 px-2 py-2 rounded-full hover:bg-primary transition-colors">
                    <Link
                      target="_blank"
                      href="https://www.youtube.com/@KissaShuruTalkies"
                      className="text-white hover:bg-white/10 rounded-full"
                      aria-label="Visit our YouTube channel"
                    >
                      <Youtube className="w-5 h-5" />
                    </Link>
                  </div>
                  <div className="bg-gray-500 px-2 py-2 rounded-full hover:bg-primary transition-colors">
                    <Link
                      target="_blank"
                      href="https://x.com/KissaShuru"
                      className="text-white hover:bg-white/10 rounded-full"
                      aria-label="Visit our Twitter profile"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-5 h-5"
                      >
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.494 11.24H16.21l-5.214-6.822-5.97 6.822H1.717l7.73-8.836L1.254 2.25H8.04l4.713 6.231 5.49-6.231Zm-1.164 18.038h1.833L7.084 4.356H5.117l11.963 15.932Z" />
                      </svg>
                    </Link>
                  </div>


                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <ScrollToTop />
    </>
  );
};

export default Footer;
